import { useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useDocumentState } from '@/state'
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual'

type Args = {
  scrollEl: HTMLElement | null
  virtualizer: Virtualizer<HTMLDivElement, Element>
  virtualItems: VirtualItem[]
  pageIds: string[]
  zoom: number
  debounceMs?: number
}

export function useActivePageFromVirtualizer({
  scrollEl,
  virtualizer,
  virtualItems,
  pageIds,
  zoom,
  debounceMs = 0,
}: Args) {
  const setCurrentPageInView = useDocumentState(
    useShallow((s) => s.setCurrentPageInView),
  )

  const rafRef = useRef<number | null>(null)
  const tRef = useRef<number | null>(null)

  useEffect(() => {
    if (!scrollEl || !virtualizer) return

    const compute = () => {
      const top = scrollEl.scrollTop
      const bottom = scrollEl.scrollTop + scrollEl.clientHeight

      let bestIdx: number | undefined
      let bestOverlap = -1

      for (const it of virtualItems) {
        const start = it.start
        const end = start + it.size
        const overlap = Math.max(
          0,
          Math.min(end, bottom) - Math.max(start, top),
        )
        if (overlap > bestOverlap) {
          bestOverlap = overlap
          bestIdx = it.index
        }
      }

      if (bestIdx != null && bestIdx >= 0 && bestIdx < pageIds.length) {
        const id = pageIds[bestIdx]
        if (id) {
          setCurrentPageInView(id)

          // Update the URL search param to keep it in sync
          const currentUrl = new URL(window.location.href)
          const currentPageId = currentUrl.searchParams.get('page_id')

          // Only update if the page_id has changed to avoid unnecessary history entries
          if (currentPageId !== id) {
            currentUrl.searchParams.set('page_id', id)
            window.history.replaceState(
              {},
              '',
              currentUrl.pathname + currentUrl.search,
            )
          }
        }
      }
    }

    const schedule = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      const run = () => compute()
      if (debounceMs > 0) {
        if (tRef.current) window.clearTimeout(tRef.current)
        tRef.current = window.setTimeout(run, debounceMs)
      } else {
        rafRef.current = requestAnimationFrame(run)
      }
    }

    const onScroll = () => schedule()

    scrollEl.addEventListener('scroll', onScroll, { passive: true })

    schedule()

    return () => {
      scrollEl.removeEventListener('scroll', onScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (tRef.current) window.clearTimeout(tRef.current)
    }
  }, [
    scrollEl,
    virtualizer,
    virtualItems,
    pageIds,
    zoom,
    debounceMs,
    setCurrentPageInView,
  ])
}
